import { useLazyQuery, useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import * as yup from "yup";

import BaseErrors from "@components/fields/BaseErrors";
import EmployeeSelect from "@components/fields/EmployeeSelect";
import EmployerSelect from "@components/fields/EmployerSelect";
import FormButtons from "@components/fields/FormButtons";
import Input from "@components/fields/Input";
import Form from "@components/forms/Form";
import FormCondition from "@components/forms/FormCondition";
import {
  EligibilityMethodEnum,
  GetEmployerDocument,
  GetEmployerQuery,
  GetEmployerQueryVariables,
  PackageGrantFragment as PackageGrant,
  PackageGrantEligibilityDecisionCreateDocument,
  PackageGrantEligibilityDecisionCreateMutation,
  PackageGrantEligibilityDecisionCreateMutationVariables,
  PackageGrantEligibilityDecisionEnum
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";

type FormData = {
  employeeId?: string | null;
  employerId: string | null;
  notes: string;
};

type Props = {
  onError: () => void;
  onSuccess?: () => void;
  packageGrant: PackageGrant;
};

const PackageGrantEligibilityDecisionForm = ({ onError, onSuccess, packageGrant }: Props) => {
  const [submitting, setSubmitting] = useState(false);

  const defaultValues: FormData = {
    employeeId: packageGrant.client.employee?.id ?? null,
    employerId: packageGrant.client.employer?.id ?? null,
    notes: packageGrant?.eligibilityNotes ?? ""
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [createEligibilityDecision] = useMutation<
    PackageGrantEligibilityDecisionCreateMutation,
    PackageGrantEligibilityDecisionCreateMutationVariables
  >(PackageGrantEligibilityDecisionCreateDocument);

  const [getEmployer, getEmployerQuery] = useLazyQuery<GetEmployerQuery, GetEmployerQueryVariables>(
    GetEmployerDocument
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      employeeId: yup.string().nullable(),
      employerId: yup.string().required(intl.formatMessage({ id: "errors.eligibility.employerId.blank" })),
      notes: yup.string().defined()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const initialEmployerOption = useMemo(() => {
    if (packageGrant.client.employer) {
      return {
        label: packageGrant.client.employer.name,
        value: packageGrant.client.employer.id
      };
    }
    return {
      label: undefined,
      value: null
    };
  }, [packageGrant.client.employer]);

  const initialEmployeeOption = useMemo(() => {
    if (packageGrant.client.employee) {
      const employee = packageGrant.client.employee;
      return {
        label: `${employee.firstName} ${employee.lastName} (${employee.workEmail})`,
        value: packageGrant.client.employee.id
      };
    }
    return {
      label: undefined,
      value: null
    };
  }, [packageGrant.client.employee]);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    setSubmitting(true);
    return createEligibilityDecision({
      variables: {
        ...form.getValues(),
        decision: PackageGrantEligibilityDecisionEnum.APPROVE,
        packageGrantId: packageGrant.id
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.packageGrantEligibilityDecisionCreate,
        intl,
        onError,
        onSuccess: () => onSuccess?.(),
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.eligibility"
      });
      setSubmitting(false);
    });
  }, [createEligibilityDecision, form, intl, onError, onSuccess, packageGrant.id]);

  const handleReject = useCallback(() => {
    setBaseErrors([]);
    setSubmitting(true);
    createEligibilityDecision({
      variables: {
        decision: PackageGrantEligibilityDecisionEnum.REJECT,
        notes: form.getValues().notes,
        packageGrantId: packageGrant.id
      }
    }).then(() => {
      onSuccess?.();
      setSubmitting(false);
    });
  }, [createEligibilityDecision, form, onSuccess, packageGrant.id]);

  const employerId = form.watch("employerId");

  useEffect(() => {
    if (employerId) {
      getEmployer({ variables: { id: employerId } });
    }
  }, [employerId, getEmployer]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <EmployerSelect<FormData>
        allowAny={false}
        allowSelfPaid={false}
        control={form.control}
        error={form.formState.errors.employerId}
        initialOption={initialEmployerOption}
        label={intl.formatMessage({ id: "forms.eligibility.employerIdLabel" })}
        name="employerId"
        required
      />
      <FormCondition
        condition={
          employerId !== null &&
          getEmployerQuery.data?.employer.eligibilityMethod === EligibilityMethodEnum.ELIGIBILITY_FILE
        }
        fieldName="employeeId"
        form={form}
      >
        <EmployeeSelect<FormData>
          control={form.control}
          employerId={employerId as string}
          error={form.formState.errors.employeeId}
          initialOption={initialEmployeeOption}
          label={intl.formatMessage({ id: "forms.eligibility.employeeIdLabel" })}
          name="employeeId"
        />
      </FormCondition>
      <Input
        error={form.formState.errors.notes}
        label={intl.formatMessage({ id: "forms.eligibility.notesLabel" })}
        register={form.register("notes")}
      />
      <div>
        <div>
          <FormButtons
            cancelKey="dictionary.reject"
            hookForm={form}
            onCancel={handleReject}
            saveDisabled={submitting}
            saveKey="dictionary.approve"
            skipDirty
          />
        </div>
      </div>
    </Form>
  );
};

export default PackageGrantEligibilityDecisionForm;
